/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CheckIndicator, Loader, SdiAdd, styles, TextLink } from '@sdir/sds';
import styled from 'styled-components';
import { generatePath } from 'react-router-dom';
import { ErrorMessage, SortableDataTable } from '@sdir/utilities/lib/components';
import {
  AttachmentResult,
  AttachmentType,
  SimulatorResultItem,
} from '@sdir/httpclient/lib/clients/aps/application';
import { RequirementStatus } from '../../../types/requirements';
import { ApplicationLayout } from '../../Organisms';
import { RequirementRow, RequirementsTableHeader } from '../../../helpers/TableHeaders';
import { AttachmentTypeTranslationMap } from '../../../constants/translationMaps';
import { ApplicationStatus, QualificationType } from '../../../types/enums';
import { useAddedQualifications, useCurrentApplication } from '../../../services/hooks';
import { routes } from '../../../constants/routes';
import { attachmentIsValid } from '../../../helpers/Qualifications';
import { RequirementAction } from '../../Molecules/AttachmentGroupActions/RequirementAction';
import { ExistingGroupAction } from '../../Molecules/AttachmentGroupActions/ExistingGroupAction';
import { qualificationsApi } from '../../../httpclient';
import {
  calcStatusForReq,
  groupAttachments,
  statusTranslationMap,
  typeTranslationMap,
} from './helpers';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

const sortableKeys = (fm) =>
  RequirementsTableHeader(fm)
    .map((x) => x.key)
    .filter((x) => x !== 'uploadAction');

export const RequirementsPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();

  const [inputErrors, setInputErrors] = useState<string[]>([]);
  const [hideCompleted, setHideCompleted] = useState(false);
  const { application, qualifications, loadQualifications, refresh } = useCurrentApplication({
    includeQualifications: true,
  });
  const { addedSeagoing, addedNewSeagoing } = useAddedQualifications({ all: true });
  const [updateStateError, setUpdateStateError] = useState<Error | null>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const applicationRow = useMemo<RequirementRow | null>(() => {
    if (!application.result) return null;
    const applicationAttachments =
      application.result.attachments?.filter((a) => a.type === AttachmentType.Application) || [];
    return {
      qualified: <CheckIndicator status={applicationAttachments.length ? 'ok' : null} />,
      qualifiedValue: applicationAttachments.length ? 0 : 1,
      type: intl.formatMessage({ id: 'app.requirements.type.application' }),
      group: intl.formatMessage({ id: 'app.requirements.group.basis' }),
      requirement: intl.formatMessage(
        { id: 'app.requirements.table.applicationrequirement' },
        { certificate: application.result.certificate }
      ),
      status: intl.formatMessage({ id: statusTranslationMap[RequirementStatus.Avventer] }),
      uploadAction: applicationAttachments.length ? (
        <ExistingGroupAction
          applicationUid={application.result.uid || ''}
          attachmentGroup={applicationAttachments[0].group || ''}
          attachments={applicationAttachments}
          type={AttachmentType.Application}
        />
      ) : null,
    };
  }, [application.result, intl]);

  const requirements = useMemo(() => {
    if (!qualifications.result || !application.result) {
      return [];
    }

    const flatRequirements = (
      qualifications.result.base?.items?.map<[SimulatorResultItem, number | null]>((q) => [
        q,
        null,
      ]) || []
    ).concat(
      qualifications.result.alternatives?.flatMap(
        (a, i) => a.items?.map<[SimulatorResultItem, number | null]>((q) => [q, i + 1]) || []
      ) || []
    );

    const filteredRequirements = hideCompleted
      ? flatRequirements.filter(([r]) => !r.status)
      : flatRequirements;

    return filteredRequirements.map<RequirementRow>(([r, altNum]) => {
      const type = r.type || QualificationType.Other;
      const attachments =
        application.result.attachments?.filter((a) => a.simulatorResultItemUri === r.uri) || [];
      const hasValidInfo =
        !!attachments.length &&
        !!attachments.find((a) =>
          attachmentIsValid(a, {
            seagoing: addedSeagoing.result || [],
            newSeagoing: addedNewSeagoing.result || {},
          })
        );
      const reqStatus = calcStatusForReq(r, hasValidInfo);
      const reqIsValid = r.status || hasValidInfo;
      return {
        qualified: <CheckIndicator status={reqIsValid ? 'ok' : null} />,
        qualifiedValue: reqIsValid ? 1 : 0,
        group: altNum
          ? intl.formatMessage({ id: 'app.requirements.group.alternative' }, { number: altNum })
          : intl.formatMessage({ id: 'app.requirements.group.basis' }),
        type: typeTranslationMap[type]
          ? intl.formatMessage({ id: typeTranslationMap[type] })
          : r.type || '',
        requirement: r.description || '',
        status: intl.formatMessage({ id: statusTranslationMap[reqStatus] }),
        uploadAction: (
          <RequirementAction
            applicationUid={application.result?.uid || ''}
            requirement={r}
            groupedAttachments={groupAttachments(attachments)}
          />
        ),
      };
    });
  }, [
    qualifications.result,
    application.result,
    hideCompleted,
    intl,
    addedSeagoing.result,
    addedNewSeagoing.result,
  ]);

  const otherDocuments = useMemo(() => {
    if (!application.result) return [];

    const otherAttachments = application.result.attachments?.filter((a) => {
      if (a.simulatorResultItemUri) return false;
      if (a.type === AttachmentType.Application) {
        return false;
      }
      if (
        hideCompleted &&
        attachmentIsValid(a, {
          seagoing: addedSeagoing.result || [],
          newSeagoing: addedNewSeagoing.result || {},
        })
      ) {
        return false;
      }
      return true;
    });

    const groupedOtherAttachments = groupAttachments(otherAttachments || []);

    return Object.entries(groupedOtherAttachments).map<RequirementRow>(([group, attachments]) => {
      const invalidAttachment = attachments.find(
        (a) =>
          !attachmentIsValid(a, {
            seagoing: addedSeagoing.result || [],
            newSeagoing: addedNewSeagoing.result || {},
          })
      );
      const groupIsValid = !invalidAttachment;
      const type = attachments[0].type || AttachmentType.Na;
      return {
        qualified: <CheckIndicator status={groupIsValid ? 'ok' : 'warning'} />,
        qualifiedValue: groupIsValid ? 0 : 2,
        type: intl.formatMessage({ id: AttachmentTypeTranslationMap[type] }),
        group: intl.formatMessage({ id: 'app.requirements.group.additional' }),
        requirement: '-',
        status: intl.formatMessage({
          id: statusTranslationMap[
            groupIsValid ? RequirementStatus.Avventer : RequirementStatus.Mangler
          ],
        }),
        uploadAction: (
          <ExistingGroupAction
            applicationUid={application.result.uid || ''}
            attachmentGroup={group}
            attachments={attachments}
            type={attachments[0].type || AttachmentType.Na}
          />
        ),
      };
    });
  }, [application.result, hideCompleted, addedSeagoing.result, addedNewSeagoing.result, intl]);

  const tableData = useMemo(() => {
    const baseList = applicationRow ? [applicationRow] : [];
    return [...baseList, ...requirements, ...otherDocuments];
  }, [applicationRow, requirements, otherDocuments]);

  const toggleHideCompleted = useCallback(
    () => setHideCompleted(!hideCompleted),
    [setHideCompleted, hideCompleted]
  );

  const handleUploadOtherDocs = useCallback(() => {
    const path = generatePath(routes.application.addFiles, {
      applicationUid: application?.result?.uid || '',
    });
    navigate(path);
  }, [application?.result, navigate]);

  const validateAddedFiles = useCallback(
    (attachments: AttachmentResult[]): Promise<boolean> => {
      setInputErrors([]);
      const errors = attachments
        .filter(
          (x) =>
            !attachmentIsValid(x, {
              seagoing: addedSeagoing.result || [],
              newSeagoing: addedNewSeagoing.result || {},
            })
        )
        .map((x) =>
          intl.formatMessage(
            { id: 'app.requirements.errors.missingfiledata' },
            { filename: x.blobName }
          )
        );
      if (!errors.length) return Promise.resolve(true);

      // use set timeout to allow "closed" error messages to be reappear (SDS behaviour)
      return new Promise((resolve) => {
        setTimeout(() => {
          setInputErrors(errors);
          resolve(!errors.length);
        });
      });
    },
    [addedSeagoing.result, addedNewSeagoing.result, intl]
  );

  const moreInfoRequested =
    application?.result?.applicationStatus === ApplicationStatus.RequestMoreInformation;

  const handleNextClick = useCallback(async () => {
    setUpdateStateError(null);
    setIsSubmitting(true);
    if (!application.result) return;

    const isValid = await validateAddedFiles(application.result.attachments || []);

    if (!isValid) return;

    if (moreInfoRequested) {
      qualificationsApi
        .v1QualificationsSetqualificationsupdatedQueryPost({
          applicationUid: application.result.uid || '',
        })
        .then(() => {
          setIsSubmitting(false);
          refresh?.();
          navigate(
            generatePath(routes.application.details, {
              applicationUid: application.result.uid || '',
            })
          );
        })
        .catch((e) => {
          setIsSubmitting(false);
          setUpdateStateError(e);
        });
    }
  }, [application.result, navigate, moreInfoRequested, refresh, validateAddedFiles]);

  const isLoading = application.loading || qualifications.loading || addedSeagoing.loading;

  return (
    <ApplicationLayout
      topHeading={intl.formatMessage({ id: 'app.requirements.table.title' })}
      heading={intl.formatMessage({ id: 'app.requirements.table.header' })}
      subHeading={intl.formatMessage({ id: 'app.requirements.title' })}
      exitLabel={intl.formatMessage({ id: 'application.upload.missing.documentation.go.back' })}
      canNavBack
      canNavNext={!!requirements}
      onNavNextClick={handleNextClick}
      navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
      navNextLabel={intl.formatMessage({ id: 'general.form.submit' })}
    >
      {isLoading && (
        <div data-testid="app-loader">
          <Loader />
        </div>
      )}
      {qualifications.error && (
        <ErrorMessage
          title={intl.formatMessage({ id: 'app.requirements.title' })}
          error={qualifications.error}
        >
          <TextLink
            text={intl.formatMessage({ id: 'general.button.retry' })}
            onClick={loadQualifications}
          />
        </ErrorMessage>
      )}
      {addedSeagoing.error && (
        <ErrorMessage
          title={intl.formatMessage({ id: 'app.requirements.title' })}
          error={addedSeagoing.error}
        />
      )}
      {updateStateError && (
        <ErrorMessage
          title={intl.formatMessage({ id: 'app.requirements.state.update.error' })}
          error={updateStateError}
        />
      )}
      {!isLoading && (
        <>
          <TableHeader>
            <h3>
              {intl.formatMessage(
                { id: 'app.requirements.obligatoryreqsfor' },
                { certificate: application.result?.certificate }
              )}
            </h3>
            <TextLink
              text={
                hideCompleted
                  ? intl.formatMessage({ id: 'app.requirements.showall' })
                  : intl.formatMessage({ id: 'app.requirements.hidecompleted' })
              }
              click={toggleHideCompleted}
            />
          </TableHeader>
          {tableData && (
            <StyledSortableDataTable
              data={tableData}
              columns={RequirementsTableHeader(intl.formatMessage)}
              sortableKeys={sortableKeys(intl.formatMessage)}
            />
          )}
          <BottomControls>
            <TextLink
              icon={<SdiAdd size="s" />}
              text={intl.formatMessage({ id: 'app.requirements.uploadotherdocuments' })}
              click={handleUploadOtherDocs}
            />
          </BottomControls>
          <ErrorsWrapper>
            {inputErrors.map((x, i) => (
              <ErrorMessage key={i} error={x} />
            ))}
          </ErrorsWrapper>
          {(isLoading || isSubmitting) && <Loader />}
        </>
      )}
    </ApplicationLayout>
  );
};

export default RequirementsPage;

const TableHeader = styled.div`
  background: ${styles.colors.primary10};
  display: flex;
  align-items: center;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  height: 10rem;
  padding: 0 3rem;

  > * {
    flex: 1;

    &:last-child {
      flex: 0 0 auto;
    }
  }

  h3 {
    margin: 0;
    font-family: ${styles.fonts.stfMedium};
    font-size: 3rem;
  }
`;

const ErrorsWrapper = styled.div`
  > * {
    margin: 2rem 0;
  }
`;

const StyledSortableDataTable = styled(SortableDataTable)`
  box-shadow: 0 3px 3px #33333333;
  > div {
    border-top-left-radius: 0;
    border-top-right-radius: 0;

    table > tbody > tr > td {
      padding: 0.75em 0.5em;

      &:nth-child(3) {
        color: #000;
      }
    }
  }
`;

const BottomControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
`;
